import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { Privileges } from 'src/app/privileges/privileges';
import { getSelectedAccount } from 'src/app/store/select-account/select-account.selector';
import { AddClosedExceptionComponent } from '../../add-closed-exception/containers/add-closed-exception.component';
import { AddScheduleHourComponent } from '../../add-schedule-hour/containers/add-schedule-hour.component';
import {
   GetPhoneNumbersUsingSchedule,
   GetPhoneNumbersUsingScheduleVariables,
} from '../queries/generated/GetPhoneNumbersUsingSchedule';
import { GetScheduleDetail, GetScheduleDetailVariables } from '../queries/generated/GetScheduleDetail';
import { getPhoneNumbersUsingDefaultSchedule, getScheduleDetail } from '../queries/schedule-detail.graphql';
import { ScheduleDetailApiService } from '../services/schedule-detail-api.service';
import * as ScheduleDetailActions from './schedule-detail.actions';
@Injectable()
export class ScheduleDetailEffects {
   loadScheduleDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            ScheduleDetailActions.scheduleDetailLoad,
            ScheduleDetailActions.saveScheduleDetailSuccess,
            ScheduleDetailActions.scheduleDetailCancel,
            ScheduleDetailActions.updateScheduleId
         ),
         withLatestFrom(this.twoColumnLayoutService.selectedId$, this.twoColumnLayoutService.firstItemId$),
         switchMap(([action, selectedId, firstItemId]) => {
            if (selectedId === null) {
               return of(ScheduleDetailActions.loadPageNoSchedule());
            }

            this.twoColumnLayoutService.setShouldShowDetailView(firstItemId !== null);

            const scheduleId = selectedId;

            return this.apiService
               .query<GetScheduleDetail, GetScheduleDetailVariables>({
                  query: getScheduleDetail,
                  variables: { id: scheduleId },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  switchMap(([scheduleResult, privileges, route]) => {
                     const schedule = scheduleResult.data.schedule;

                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageSchedules_View,
                           schedule.account.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return of(ScheduleDetailActions.scheduleDetailAccessDenied());
                     }

                     if (schedule.account.scheduleId === schedule.scheduleId) {
                        return this.apiService
                           .query<GetPhoneNumbersUsingSchedule, GetPhoneNumbersUsingScheduleVariables>({
                              query: getPhoneNumbersUsingDefaultSchedule,
                              variables: {
                                 filter: `scheduleId is null and accountId = ${schedule.accountId} and isArchived = false`,
                              },
                           })
                           .pipe(
                              map((phoneNumbersResult) => {
                                 const mergedPhoneNumbers = [
                                    ...(schedule.phoneNumbersConnection?.items ?? []),
                                    ...(phoneNumbersResult.data.phoneNumbersConnection?.items ?? []),
                                 ];

                                 const uniquePhoneNumbers = mergedPhoneNumbers.filter(
                                    (pn, index, self) =>
                                       self.findIndex((p) => p.phoneNumberId === pn.phoneNumberId) === index
                                 );

                                 return ScheduleDetailActions.loadScheduleDetailSuccess({
                                    data: {
                                       ...schedule,
                                       phoneNumbersConnection: {
                                          ...schedule.phoneNumbersConnection,
                                          items: uniquePhoneNumbers,
                                       },
                                    },
                                 });
                              }),
                              catchError((error) => of(ScheduleDetailActions.loadScheduleDetailFailure(error)))
                           );
                     }

                     return of(
                        ScheduleDetailActions.loadScheduleDetailSuccess({
                           data: schedule,
                        })
                     );
                  }),
                  catchError((error) => {
                     return of(ScheduleDetailActions.loadScheduleDetailFailure(error));
                  })
               );
         })
      );
   });

   saveScheduleDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(ScheduleDetailActions.saveScheduleDetail),
         switchMap((action) => {
            return this.scheduleDetailApiService.save(action.schedule, action.scheduleFields).pipe(
               switchMap((result) => {
                  const savedSchedule = result.data;
                  const closedExceptionsToCopy =
                     action.schedule.closedExceptions?.filter((e) => e.schedulesToCopyTo?.length) || [];

                  if (closedExceptionsToCopy.length) {
                     return this.scheduleDetailApiService.copyClosedException(closedExceptionsToCopy).pipe(
                        map(() => ScheduleDetailActions.saveScheduleDetailSuccess({ data: savedSchedule })),
                        catchError((error) => of(ScheduleDetailActions.saveScheduleDetailFailure(error)))
                     );
                  }
                  return of(ScheduleDetailActions.saveScheduleDetailSuccess({ data: savedSchedule }));
               }),
               catchError((error) => of(ScheduleDetailActions.saveScheduleDetailFailure(error)))
            );
         })
      );
   });

   deleteSchedule = createEffect(() => {
      return this.actions$.pipe(
         ofType(ScheduleDetailActions.deleteSchedule),
         switchMap((action) => {
            const message = 'Are you sure you would like to delete this Schedule?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Schedule?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.scheduleDetailApiService.deleteSchedule(action.scheduleId).pipe(
                        map(() => {
                           return ScheduleDetailActions.deleteScheduleSuccess(action);
                        }),
                        catchError((error) => {
                           return of(ScheduleDetailActions.deleteScheduleFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteScheduleSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ScheduleDetailActions.deleteScheduleSuccess),
            withLatestFrom(this.store.pipe(select(selectRouteParams))),
            mergeMap(([action]) => {
               this.twoColumnLayoutService.itemDeleted();
               this.snackBar.open(`Schedule ${action.name} deleted`);

               return [ScheduleDetailActions.scheduleDetailLoad()];
            })
         );
      },
      { dispatch: true }
   );

   saveScheduleDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ScheduleDetailActions.saveScheduleDetailSuccess),
            tap(() => {
               this.snackBar.open('Schedule Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   addScheduleHour = createEffect(() => {
      return this.actions$.pipe(
         ofType(ScheduleDetailActions.addScheduleHour),
         switchMap((action) => {
            return this.openScheduleHourDialog();
         })
      );
   });

   addClosedException = createEffect(() => {
      return this.actions$.pipe(
         ofType(ScheduleDetailActions.addClosedException),
         withLatestFrom(this.store.pipe(select(getSelectedAccount))),
         switchMap(([action, account]) => {
            return this.openClosedExceptionDialog(account.accountId, action.scheduleId);
         })
      );
   });

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private scheduleDetailApiService: ScheduleDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private dialogService: LcsDialogService,
      private location: Location,
      private twoColumnLayoutService: TwoColumnLayoutService
   ) {}

   openScheduleHourDialog(): Observable<Action> {
      const dialogRef = this.dialogService.openFormComponent(AddScheduleHourComponent, {
         closeOnNavigation: true,
         data: {},
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed().pipe(
         filter((scheduleHourItem) => {
            return scheduleHourItem !== undefined;
         }),
         map((scheduleHourItem) => {
            return ScheduleDetailActions.scheduleHourItemDialogSubmit({ scheduleHourItem: scheduleHourItem });
         })
      );
   }

   openClosedExceptionDialog(accountId: number | null, scheduleId: number | null): Observable<Action> {
      const dialogRef = this.dialogService.openFormComponent(AddClosedExceptionComponent, {
         closeOnNavigation: true,
         data: {
            accountId,
            scheduleId,
            includeReusableOption: true,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed().pipe(
         filter((closedExceptionItem) => {
            return closedExceptionItem !== undefined;
         }),
         map((closedExceptionItem) => {
            return ScheduleDetailActions.closedExceptionItemDialogSubmit({ closedExceptionItem: closedExceptionItem });
         })
      );
   }
}
